/**
 * SCS Analytics
 * Specific analytics helpers for Store Config Service
 *
 * IMPORTANT: All analytics functions are wrapped in try-catch to ensure
 * they NEVER impact main application functionality if they fail.
 */

import { sendNewRelicEvent, sendNewRelicError } from '../newrelic';

import { SCS_EVENTS, FEATURES } from './constants';
import { trackFeatureUsage } from './sessionTracker';

/**
 * Safe wrapper for analytics calls - ensures errors never propagate
 * @param {Function} fn - Analytics function to execute
 * @param {string} fnName - Function name for debugging
 */
const safeAnalyticsCall = (fn, fnName) => {
  try {
    fn();
  } catch (error) {
    // Silently fail - analytics should never break the app
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[SCS Analytics] ${fnName} failed:`, error);
    }
  }
};

/**
 * Safe wrapper for trackFeatureUsage - ensures errors never propagate
 */
const safeTrackFeatureUsage = (featureName, action, context = {}) => {
  try {
    trackFeatureUsage(featureName, action, context);
  } catch (error) {
    // Silently fail
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV === 'development') {
      console.warn('[SCS Analytics] trackFeatureUsage failed:', error);
    }
  }
};

/**
 * Track SCS page viewed
 * @param {object} params - Page view parameters
 */
export const trackScsPageViewed = ({ userGroups = [], isReadOnly = false }) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SCS, 'pageViewed');
    sendNewRelicEvent(SCS_EVENTS.PAGE_VIEWED, {
      featureName: FEATURES.SCS,
      isReadOnly,
      timestamp: new Date().toISOString(),
      userGroups,
    });
  }, 'trackScsPageViewed');
};

/**
 * Track country selection
 * @param {object} params - Country selection parameters
 */
export const trackScsCountrySelected = ({ country, previousCountry = null }) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.COUNTRY_SELECTED, {
      country,
      featureName: FEATURES.SCS,
      previousCountry,
    });
  }, 'trackScsCountrySelected');
};

/**
 * Track store selection
 * @param {object} params - Store selection parameters
 */
export const trackScsStoreSelected = ({
  storeId, storeNumber, country, region, brand,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SCS, 'storeSelected', { storeId });
    sendNewRelicEvent(SCS_EVENTS.STORE_SELECTED, {
      brand,
      country,
      featureName: FEATURES.SCS,
      region,
      storeId,
      storeNumber,
    });
  }, 'trackScsStoreSelected');
};

/**
 * Track AWS region selection
 * @param {object} params - Region selection parameters
 */
export const trackScsRegionSelected = ({ region, storeId = null }) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.REGION_SELECTED, {
      featureName: FEATURES.SCS,
      region,
      storeId,
    });
  }, 'trackScsRegionSelected');
};

/**
 * Track config fetch success
 * @param {object} params - Config fetch parameters
 */
export const trackScsConfigFetched = ({
  storeId, region, configCount, loadTimeMs,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.CONFIG_FETCHED, {
      configCount,
      featureName: FEATURES.SCS,
      loadTimeMs,
      region,
      storeId,
    });
  }, 'trackScsConfigFetched');
};

/**
 * Track config fetch error
 * @param {object} params - Error parameters
 */
export const trackScsConfigFetchError = ({ storeId, region, errorMessage }) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.CONFIG_FETCH_ERROR, {
      errorMessage,
      featureName: FEATURES.SCS,
      region,
      storeId,
    });
    sendNewRelicError(new Error(errorMessage || 'Unknown error'), {
      feature: FEATURES.SCS,
      operation: 'configFetch',
      region,
      storeId,
    });
  }, 'trackScsConfigFetchError');
};

/**
 * Track key search
 * @param {object} params - Search parameters
 */
export const trackScsKeySearched = ({ searchTerm, resultsCount, storeId }) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.KEY_SEARCHED, {
      featureName: FEATURES.SCS,
      resultsCount,
      searchTerm,
      storeId,
    });
  }, 'trackScsKeySearched');
};

// Store edit start times for duration calculation
const editStartTimes = new Map();

/**
 * Track key edit started
 * @param {object} params - Edit parameters
 */
export const trackScsKeyEditStarted = ({
  keyName, group, currentValue, currentScope, storeId,
}) => {
  safeAnalyticsCall(() => {
    // Store start time for duration calculation
    if (keyName) {
      editStartTimes.set(keyName, Date.now());
    }

    safeTrackFeatureUsage(FEATURES.SCS, 'keyEditStarted', { keyName });
    sendNewRelicEvent(SCS_EVENTS.KEY_EDIT_STARTED, {
      currentScope,
      currentValue: currentValue != null ? String(currentValue).substring(0, 100) : '',
      featureName: FEATURES.SCS,
      group,
      keyName,
      storeId,
    });
  }, 'trackScsKeyEditStarted');
};

/**
 * Get duration since edit started for a key
 * @param {string} keyName - The key name
 * @returns {number|null} Duration in milliseconds or null
 */
export const getEditDuration = (keyName) => {
  try {
    if (!keyName) return null;
    const startTime = editStartTimes.get(keyName);
    if (startTime) {
      const duration = Date.now() - startTime;
      editStartTimes.delete(keyName); // Clean up
      return duration;
    }
    return null;
  } catch (error) {
    // Silently fail
    return null;
  }
};

/**
 * Track scope change
 * @param {object} params - Scope change parameters
 */
export const trackScsKeyScopeChanged = ({ keyName, oldScope, newScope }) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.KEY_SCOPE_CHANGED, {
      featureName: FEATURES.SCS,
      keyName,
      newScope,
      oldScope,
    });
  }, 'trackScsKeyScopeChanged');
};

/**
 * Track config saved successfully
 * @param {object} params - Save parameters
 */
export const trackScsConfigSaved = ({
  keyName, scopeType, scope, valueType, storeId, region, hasJustification, userEmail = null, durationMs = null,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SCS, 'configSaved', { keyName, scopeType });
    sendNewRelicEvent(SCS_EVENTS.CONFIG_SAVED, {
      durationMs,
      featureName: FEATURES.SCS,
      hasJustification,
      keyName,
      region,
      scope,
      scopeType,
      storeId,
      timestamp: new Date().toISOString(),
      userEmail,
      valueType,
    });
  }, 'trackScsConfigSaved');
};

/**
 * Track config save error
 * @param {object} params - Error parameters
 */
export const trackScsConfigSaveError = ({
  keyName, scopeType, storeId, region, errorMessage, errorCode, userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.CONFIG_SAVE_ERROR, {
      errorCode,
      errorMessage,
      featureName: FEATURES.SCS,
      keyName,
      region,
      scopeType,
      storeId,
      userEmail,
    });
    sendNewRelicError(new Error(errorMessage || 'Unknown error'), {
      errorCode,
      feature: FEATURES.SCS,
      keyName,
      operation: 'configSave',
      region,
      storeId,
    });
  }, 'trackScsConfigSaveError');
};

/**
 * Track scope diff check triggered
 * @param {object} params - Diff check parameters
 */
export const trackScsScopeDiffCheckTriggered = ({ keyName, scopeType, conflictsCount }) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.SCOPE_DIFF_CHECK_TRIGGERED, {
      conflictsCount,
      featureName: FEATURES.SCS,
      keyName,
      scopeType,
    });
  }, 'trackScsScopeDiffCheckTriggered');
};

/**
 * Track scope diff check bulk update success
 * @param {object} params - Bulk update parameters
 */
export const trackScsScopeDiffCheckBulkUpdateSuccess = ({
  keyName,
  selectedCount,
  successCount,
  userEmail,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.SCOPE_DIFF_CHECK_BULK_UPDATE_SUCCESS, {
      featureName: FEATURES.SCS,
      keyName,
      selectedCount,
      successCount,
      userEmail,
    });
  }, 'trackScsScopeDiffCheckBulkUpdateSuccess');
};

/**
 * Track scope diff check bulk update error
 * @param {object} params - Error parameters
 */
export const trackScsScopeDiffCheckBulkUpdateError = ({
  keyName,
  selectedCount,
  errorMessage,
  userEmail,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.SCOPE_DIFF_CHECK_BULK_UPDATE_ERROR, {
      errorMessage,
      featureName: FEATURES.SCS,
      keyName,
      selectedCount,
      userEmail,
    });
  }, 'trackScsScopeDiffCheckBulkUpdateError');
};

/**
 * Track scope diff check downloaded
 * @param {object} params - Download parameters
 */
export const trackScsScopeDiffCheckDownloaded = ({
  keyName,
  downloadedCount,
  totalConflicts,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.SCOPE_DIFF_CHECK_DOWNLOADED, {
      downloadedCount,
      featureName: FEATURES.SCS,
      keyName,
      totalConflicts,
    });
  }, 'trackScsScopeDiffCheckDownloaded');
};

/**
 * Track scope diff check cancelled
 * @param {object} params - Cancel parameters
 */
export const trackScsScopeDiffCheckCancelled = ({ keyName, conflictsCount }) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.SCOPE_DIFF_CHECK_CANCELLED, {
      conflictsCount,
      featureName: FEATURES.SCS,
      keyName,
    });
  }, 'trackScsScopeDiffCheckCancelled');
};

/**
 * Track create key overlay opened
 */
export const trackScsCreateKeyOverlayOpened = () => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SCS, 'createKeyOverlayOpened');
    sendNewRelicEvent(SCS_EVENTS.CREATE_KEY_OVERLAY_OPENED, {
      featureName: FEATURES.SCS,
      timestamp: new Date().toISOString(),
    });
  }, 'trackScsCreateKeyOverlayOpened');
};

/**
 * Track create key overlay closed
 * @param {object} params - Close parameters
 */
export const trackScsCreateKeyOverlayClosed = ({ completed = false }) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.CREATE_KEY_OVERLAY_CLOSED, {
      completed,
      featureName: FEATURES.SCS,
    });
  }, 'trackScsCreateKeyOverlayClosed');
};

/**
 * Track key created successfully
 * @param {object} params - Key creation parameters
 */
export const trackScsKeyCreated = ({
  keyName, group, valueType, region, hasGlobalValue, userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SCS, 'keyCreated', { group, keyName });
    sendNewRelicEvent(SCS_EVENTS.KEY_CREATED, {
      featureName: FEATURES.SCS,
      group,
      hasGlobalValue,
      keyName,
      region,
      timestamp: new Date().toISOString(),
      userEmail,
      valueType,
    });
  }, 'trackScsKeyCreated');
};

/**
 * Track key creation error
 * @param {object} params - Error parameters
 */
export const trackScsKeyCreationError = ({
  keyName, errorMessage, errorCode, userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.KEY_CREATION_ERROR, {
      errorCode,
      errorMessage,
      featureName: FEATURES.SCS,
      keyName,
      userEmail,
    });
    sendNewRelicError(new Error(errorMessage || 'Unknown error'), {
      errorCode,
      feature: FEATURES.SCS,
      keyName,
      operation: 'keyCreation',
    });
  }, 'trackScsKeyCreationError');
};

// ============ Bulk Edit Analytics ============

/**
 * Track bulk edit started
 * @param {object} params - Bulk edit parameters
 */
export const trackScsBulkEditStarted = ({
  region, scopeType, selectedCount, userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SCS_BULK_EDIT, 'bulkEditStarted');
    sendNewRelicEvent(SCS_EVENTS.BULK_EDIT_STARTED, {
      featureName: FEATURES.SCS_BULK_EDIT,
      region,
      scopeType,
      selectedCount,
      userEmail,
    });
  }, 'trackScsBulkEditStarted');
};

/**
 * Track bulk edit scope selected
 * @param {object} params - Scope parameters
 */
export const trackScsBulkEditScopeSelected = ({ scopeType, region }) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.BULK_EDIT_SCOPE_SELECTED, {
      featureName: FEATURES.SCS_BULK_EDIT,
      region,
      scopeType,
    });
  }, 'trackScsBulkEditScopeSelected');
};

/**
 * Track bulk edit filter applied
 * @param {object} params - Filter parameters
 */
export const trackScsBulkEditFilterApplied = ({ filterType, filterValue, resultsCount }) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.BULK_EDIT_FILTER_APPLIED, {
      featureName: FEATURES.SCS_BULK_EDIT,
      filterType,
      filterValue,
      resultsCount,
    });
  }, 'trackScsBulkEditFilterApplied');
};

/**
 * Track bulk edit executed
 * @param {object} params - Execution parameters
 */
export const trackScsBulkEditExecuted = ({
  region, scopeType, keysCount, scopesCount, hasJustification, userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SCS_BULK_EDIT, 'bulkEditExecuted', { keysCount, scopesCount });
    sendNewRelicEvent(SCS_EVENTS.BULK_EDIT_EXECUTED, {
      featureName: FEATURES.SCS_BULK_EDIT,
      hasJustification,
      keysCount,
      region,
      scopesCount,
      scopeType,
      timestamp: new Date().toISOString(),
      userEmail,
    });
  }, 'trackScsBulkEditExecuted');
};

/**
 * Track bulk edit success
 * @param {object} params - Success parameters
 */
export const trackScsBulkEditSuccess = ({
  successCount, failedCount, totalCount, userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.BULK_EDIT_SUCCESS, {
      failedCount,
      featureName: FEATURES.SCS_BULK_EDIT,
      successCount,
      successRate: totalCount > 0 ? Math.round((successCount / totalCount) * 100) : 0,
      totalCount,
      userEmail,
    });
  }, 'trackScsBulkEditSuccess');
};

/**
 * Track bulk edit error
 * @param {object} params - Error parameters
 */
export const trackScsBulkEditError = ({
  errorMessage, keysCount, scopesCount, userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.BULK_EDIT_ERROR, {
      errorMessage,
      featureName: FEATURES.SCS_BULK_EDIT,
      keysCount,
      scopesCount,
      userEmail,
    });
    sendNewRelicError(new Error(errorMessage || 'Unknown error'), {
      feature: FEATURES.SCS_BULK_EDIT,
      keysCount,
      operation: 'bulkEdit',
      scopesCount,
    });
  }, 'trackScsBulkEditError');
};

// ============ Config By Key Analytics ============

/**
 * Track config by key searched
 * @param {object} params - Search parameters
 */
export const trackScsConfigByKeySearched = ({
  keyName, region, storesCount, userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SCS_CONFIG_BY_KEY, 'keySearched', { keyName });
    sendNewRelicEvent(SCS_EVENTS.CONFIG_BY_KEY_SEARCHED, {
      featureName: FEATURES.SCS_CONFIG_BY_KEY,
      keyName,
      region,
      storesCount,
      userEmail,
    });
  }, 'trackScsConfigByKeySearched');
};

/**
 * Track config by key filter applied
 * @param {object} params - Filter parameters
 */
export const trackScsConfigByKeyFilterApplied = ({ filterType, filterValue, resultsCount }) => {
  safeAnalyticsCall(() => {
    sendNewRelicEvent(SCS_EVENTS.CONFIG_BY_KEY_FILTER_APPLIED, {
      featureName: FEATURES.SCS_CONFIG_BY_KEY,
      filterType,
      filterValue,
      resultsCount,
    });
  }, 'trackScsConfigByKeyFilterApplied');
};

/**
 * Track config by key history viewed
 * @param {object} params - History view parameters
 */
export const trackScsConfigByKeyHistoryViewed = ({
  keyName, storeId, storeNumber, historyEntriesCount,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SCS_CONFIG_BY_KEY, 'historyViewed', { keyName, storeId });
    sendNewRelicEvent(SCS_EVENTS.CONFIG_BY_KEY_HISTORY_VIEWED, {
      featureName: FEATURES.SCS_CONFIG_BY_KEY,
      historyEntriesCount,
      keyName,
      storeId,
      storeNumber,
    });
  }, 'trackScsConfigByKeyHistoryViewed');
};

/**
 * Track config by key downloaded
 * @param {object} params - Download parameters
 */
export const trackScsConfigByKeyDownloaded = ({
  keyName, region, recordsCount, userEmail = null,
}) => {
  safeAnalyticsCall(() => {
    safeTrackFeatureUsage(FEATURES.SCS_CONFIG_BY_KEY, 'downloaded', { keyName, recordsCount });
    sendNewRelicEvent(SCS_EVENTS.CONFIG_BY_KEY_DOWNLOADED, {
      featureName: FEATURES.SCS_CONFIG_BY_KEY,
      keyName,
      recordsCount,
      region,
      timestamp: new Date().toISOString(),
      userEmail,
    });
  }, 'trackScsConfigByKeyDownloaded');
};

export default {
  // Bulk edit
  trackScsBulkEditError,
  trackScsBulkEditExecuted,
  trackScsBulkEditFilterApplied,
  trackScsBulkEditScopeSelected,
  trackScsBulkEditStarted,
  trackScsBulkEditSuccess,
  // Config by key
  trackScsConfigByKeyDownloaded,
  trackScsConfigByKeyFilterApplied,
  trackScsConfigByKeyHistoryViewed,
  trackScsConfigByKeySearched,

  trackScsConfigFetched,
  // Config operations
  trackScsConfigFetchError,
  trackScsConfigSaved,
  trackScsConfigSaveError,
  trackScsCountrySelected,
  // Key creation
  trackScsCreateKeyOverlayClosed,
  trackScsCreateKeyOverlayOpened,
  trackScsKeyCreated,
  trackScsKeyCreationError,
  trackScsKeyEditStarted,
  trackScsKeyScopeChanged,
  trackScsKeySearched,
  // Page & navigation
  trackScsPageViewed,
  trackScsRegionSelected,
  // Scope diff check
  trackScsScopeDiffCheckBulkUpdateError,
  trackScsScopeDiffCheckBulkUpdateSuccess,
  trackScsScopeDiffCheckCancelled,
  trackScsScopeDiffCheckDownloaded,
  trackScsScopeDiffCheckTriggered,
  trackScsStoreSelected,
};
